import type {
  HarnessEvent,
  HarnessAssistantEvent,
  HarnessResultEvent,
  HarnessSystemInitEvent,
  HarnessCompactBoundaryEvent,
  HarnessTaskStartedEvent,
  HarnessTaskProgressEvent,
  HarnessRateLimitEvent,
  HarnessUserQuestionEvent,
} from "../harness/index.js";
import type { TaskContext, ActivityEventSummary } from "@project/shared";
import { AGENT_STATUS_REASON_USER_QUESTION } from "@project/shared";
import type { QueryHost } from "./query-host.js";
import type { UsageInfo } from "./event-handlers.js";
import {
  API_ERROR_PATTERN,
  handleAssistantCase,
  handleResultCase,
  handleRateLimitEvent,
  handleSystemEvent,
  handleSystemSubevents,
  handleToolProgressEvent,
} from "./event-handlers.js";

/** Mutable state bag threaded through the event loop. */
interface EventLoopState {
  sessionIdStored: boolean;
  isTyping: boolean;
  retriable: boolean;
  sawApiError: boolean;
  resultSummary: string | undefined;
  rateLimitResetsAt: string | undefined;
  /** Set when a rate_limit_event arrived with status "rejected" — a hard cap,
   *  distinct from resetsAt (which the PTY banner can't always supply). */
  rateLimitRejectedType: string | undefined;
  staleSession: boolean | undefined;
  authError: boolean | undefined;
  lastAssistantUsage: UsageInfo | undefined;
  turnToolCalls: ActivityEventSummary[];
  /**
   * A TUI AskUserQuestion questionnaire is pending (armed by the PTY
   * harness's `user_question` event). While set, the periodic "running"
   * re-emit is suppressed so it can't clobber the reported
   * `waiting_for_input`. Display-only — mirrors `watchForParkedTui`.
   */
  questionPending: boolean;
}

/** The arming assistant record contains the AskUserQuestion tool_use block —
 * it can arrive via the transcript tailer after the hook event and must not
 * clear the pending flag. */
function hasAskUserQuestionBlock(event: HarnessAssistantEvent): boolean {
  return event.message.content.some((b) => b.type === "tool_use" && b.name === "AskUserQuestion");
}

/** Join the parked questionnaire's prompts into a single notification-friendly
 * string. Multiple sub-questions are newline-joined; the server slices to 200. */
function questionTextFromEvent(event: HarnessUserQuestionEvent): string | undefined {
  const text = event.questions
    .map((q) => q.question.trim())
    .filter((q) => q.length > 0)
    .join("\n");
  return text.length > 0 ? text : undefined;
}

/** Report `waiting_for_input` for a pending TUI questionnaire. Display-only:
 * SessionRunner `_state` is untouched (same semantics as `watchForParkedTui`),
 * so idle-timer and chat-supersede behavior are unchanged. The question text
 * rides along so the server can surface it in the user-question notification. */
async function armQuestionPending(
  host: QueryHost,
  state: EventLoopState,
  event: HarnessUserQuestionEvent,
): Promise<void> {
  state.questionPending = true;
  await host.connection.emitStatus(
    "waiting_for_input",
    AGENT_STATUS_REASON_USER_QUESTION,
    questionTextFromEvent(event),
  );
  await host.callbacks.onStatusChange("waiting_for_input");
}

async function clearQuestionPending(
  host: QueryHost,
  state: EventLoopState,
  options: { emitRunning: boolean },
): Promise<void> {
  if (!state.questionPending) return;
  state.questionPending = false;
  if (options.emitRunning) {
    await host.connection.emitStatus("running");
    await host.callbacks.onStatusChange("running");
  }
}

/**
 * Question-pending transitions driven by the event stream:
 *   - user_question (PTY hook observed AskUserQuestion) → arm.
 *   - tool_progress for AskUserQuestion (PostToolUse fires once the user
 *     answered in the TUI) → clear + running (the deterministic clear).
 *   - assistant WITHOUT an AskUserQuestion block → clear + running (backstop;
 *     a new assistant record only exists after the questionnaire resolved).
 *     Records WITH the block are the arming tool_use arriving late via the
 *     transcript tailer and must not clear.
 *   - result → clear silently (the result path owns the final status; covers
 *     Esc/interrupt and turn end while parked).
 */
async function applyQuestionTransitions(
  event: HarnessEvent,
  host: QueryHost,
  state: EventLoopState,
): Promise<void> {
  switch (event.type) {
    case "user_question":
      await armQuestionPending(host, state, event as HarnessUserQuestionEvent);
      return;
    case "assistant":
      if (!hasAskUserQuestionBlock(event as HarnessAssistantEvent)) {
        await clearQuestionPending(host, state, { emitRunning: true });
      }
      return;
    case "tool_progress":
      if (event.tool_name === "AskUserQuestion") {
        await clearQuestionPending(host, state, { emitRunning: true });
      }
      return;
    case "result":
      await clearQuestionPending(host, state, { emitRunning: false });
      return;
    default:
      break;
  }
}

function stopTypingIfNeeded(host: QueryHost, isTyping: boolean): void {
  if (isTyping) host.connection.sendTypingStop();
}

/** Merge pending tool outputs into accumulated tool call summaries and emit turn_end. */
function flushPendingToolCalls(host: QueryHost, turnToolCalls: ActivityEventSummary[]): void {
  if (turnToolCalls.length === 0) {
    // Clear any orphaned outputs that arrived without matching tool_use blocks
    host.pendingToolOutputs.length = 0;
    return;
  }
  // Match outputs to tool calls by tool name (handles duplicate names via queue)
  const outputsByTool = new Map<string, string[]>();
  for (const entry of host.pendingToolOutputs) {
    const list = outputsByTool.get(entry.tool) ?? [];
    list.push(entry.output);
    outputsByTool.set(entry.tool, list);
  }
  for (const call of turnToolCalls) {
    const list = outputsByTool.get(call.tool);
    if (list && list.length > 0) {
      call.output = list.shift();
    }
  }
  host.connection.sendEvent({ type: "turn_end", toolCalls: [...turnToolCalls] });
  turnToolCalls.length = 0;
  host.pendingToolOutputs.length = 0;
}

async function processSystemCase(
  event:
    | HarnessSystemInitEvent
    | HarnessCompactBoundaryEvent
    | HarnessTaskStartedEvent
    | HarnessTaskProgressEvent,
  host: QueryHost,
  context: TaskContext,
  state: EventLoopState,
): Promise<void> {
  if (event.subtype === "init") {
    const stored = await handleSystemEvent(event, host, context, state.sessionIdStored);
    if (stored) state.sessionIdStored = true;
  } else {
    handleSystemSubevents(
      event as HarnessCompactBoundaryEvent | HarnessTaskStartedEvent | HarnessTaskProgressEvent,
      host,
    );
  }
}

async function processAssistantCase(
  event: HarnessAssistantEvent,
  host: QueryHost,
  state: EventLoopState,
): Promise<void> {
  if (!state.isTyping) {
    setTimeout(() => host.connection.sendTypingStart(), 200);
    state.isTyping = true;
  }
  const usage = await handleAssistantCase(event, host, state.turnToolCalls);
  if (usage) state.lastAssistantUsage = usage;

  // Check for API error patterns in assistant message text.
  // The SDK may emit API errors as assistant message content before ending
  // with a clean success result, which would bypass the result-level retry check.
  if (!state.sawApiError) {
    const fullText = event.message.content
      .filter((b: { type: string }) => b.type === "text")
      .map((b: { type: string; text?: string }) => (b as { text: string }).text)
      .join(" ");
    if (API_ERROR_PATTERN.test(fullText)) {
      state.sawApiError = true;
    }
  }
}

async function processResultCase(
  event: HarnessResultEvent,
  host: QueryHost,
  context: TaskContext,
  startTime: number,
  state: EventLoopState,
): Promise<void> {
  const info = await handleResultCase(
    event,
    host,
    context,
    startTime,
    state.isTyping,
    state.lastAssistantUsage,
  );
  if (info.stoppedTyping) state.isTyping = false;
  state.retriable = info.retriable;
  // If the result itself is clean (not retriable), any API error the agent
  // mentioned in assistant text was already recovered from — don't retry.
  if (!info.retriable) state.sawApiError = false;
  state.resultSummary = info.resultSummary;
  if (info.staleSession) state.staleSession = true;
  if (info.authError) state.authError = true;
}

/** Record both signals a rate_limit_event carries: the reset time (when known)
 *  and whether this was a hard rejection (drives the key-cycle path). */
function processRateLimitCase(
  event: HarnessRateLimitEvent,
  host: QueryHost,
  state: EventLoopState,
): void {
  const resetsAt = handleRateLimitEvent(event, host);
  if (resetsAt) state.rateLimitResetsAt = resetsAt;
  if (event.rate_limit_info.status === "rejected") {
    state.rateLimitRejectedType = event.rate_limit_info.rateLimitType ?? "unknown";
  }
}

export async function processEvents(
  events: AsyncGenerator<HarnessEvent, void>,
  context: TaskContext,
  host: QueryHost,
): Promise<{
  retriable: boolean;
  resultSummary?: string;
  modeRestart?: boolean;
  rateLimitResetsAt?: string;
  rateLimitRejectedType?: string;
  staleSession?: boolean;
  authError?: boolean;
}> {
  const startTime = Date.now();
  let lastStatusEmit = Date.now();
  const STATUS_REEMIT_INTERVAL_MS = 5_000;

  const state: EventLoopState = {
    sessionIdStored: false,
    isTyping: false,
    retriable: false,
    sawApiError: false,
    resultSummary: undefined,
    rateLimitResetsAt: undefined,
    rateLimitRejectedType: undefined,
    staleSession: undefined,
    authError: undefined,
    lastAssistantUsage: undefined,
    turnToolCalls: [],
    questionPending: false,
  };

  for await (const event of events) {
    if (host.isStopped()) break;

    // Flush any pending tool calls from the previous assistant turn
    // (tool outputs have been collected by PostToolUse hooks between events)
    flushPendingToolCalls(host, state.turnToolCalls);

    // Re-emit "running" periodically so missed status events self-correct.
    // Suppressed while a questionnaire is pending — a stray tool_progress
    // (e.g. a parallel tool finishing) must not clobber waiting_for_input.
    const now = Date.now();
    if (now - lastStatusEmit >= STATUS_REEMIT_INTERVAL_MS && !state.questionPending) {
      host.connection.emitStatus("running");
      lastStatusEmit = now;
    }

    if (host.pendingModeRestart) {
      stopTypingIfNeeded(host, state.isTyping);
      return { retriable: false, modeRestart: true };
    }

    await applyQuestionTransitions(event, host, state);

    switch (event.type) {
      case "system":
        await processSystemCase(event as HarnessSystemInitEvent, host, context, state);
        break;
      case "assistant":
        await processAssistantCase(event as HarnessAssistantEvent, host, state);
        break;
      case "result":
        await processResultCase(event as HarnessResultEvent, host, context, startTime, state);
        break;
      case "rate_limit_event":
        processRateLimitCase(event as HarnessRateLimitEvent, host, state);
        break;
      case "tool_progress":
        handleToolProgressEvent(event, host);
        break;
    }
  }

  flushPendingToolCalls(host, state.turnToolCalls);
  stopTypingIfNeeded(host, state.isTyping);

  return {
    retriable: state.retriable || state.sawApiError,
    resultSummary: state.resultSummary,
    rateLimitResetsAt: state.rateLimitResetsAt,
    ...(state.rateLimitRejectedType && { rateLimitRejectedType: state.rateLimitRejectedType }),
    ...(state.staleSession && { staleSession: state.staleSession }),
    ...(state.authError && { authError: state.authError }),
  };
}
